<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ template ? $t('bo.sessionTemplates.editTitleWith', { title: template.title }) : $t('bo.sessionTemplates.editTitle') }}</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div v-if="loadingTemplate" class="modal-body loading-state">{{ $t('bo.common.loading') }}</div>

      <template v-else-if="template">
        <div class="modal-body">
          <div v-if="error" class="error-banner">{{ error }}</div>
          <div v-if="success" class="success-banner">{{ success }}</div>

          <!-- Status badge + actions -->
          <div class="status-row">
            <span :class="['badge', `badge-${template.status}`]">{{ template.status }}</span>
            <div class="status-actions">
              <button
                v-if="template.status === 'draft'"
                @click="handlePublish"
                :disabled="actionLoading"
                class="btn-action btn-publish"
              >{{ $t('bo.sessionTemplates.publish') }}</button>
              <button
                v-if="template.status !== 'archived'"
                @click="handleArchive"
                :disabled="actionLoading"
                class="btn-action btn-archive"
              >{{ $t('bo.sessionTemplates.archive') }}</button>
            </div>
          </div>

          <div v-if="template.status !== 'draft'" class="info-notice">
            {{ $t('bo.sessionTemplates.onlyDraftEditable') }}
          </div>

          <div class="form-group">
            <label>{{ $t('bo.sessionTemplates.labelTitle') }} <span class="required">*</span></label>
            <input
              v-model="form.title"
              type="text"
              class="input"
              :disabled="template.status !== 'draft'"
              maxlength="200"
            />
          </div>

          <div class="form-group">
            <label>{{ $t('bo.sessionTemplates.labelDescription') }}</label>
            <textarea
              v-model="form.description"
              class="textarea"
              rows="3"
              :disabled="template.status !== 'draft'"
            />
          </div>

          <div class="form-group">
            <label>{{ $t('bo.sessionTemplates.labelDuration') }}</label>
            <input
              v-model.number="form.estimated_duration"
              type="number"
              class="input input-sm"
              min="1"
              max="480"
              :disabled="template.status !== 'draft'"
            />
          </div>

          <div class="form-group">
            <label>{{ $t('bo.sessionTemplates.labelPrompts') }}</label>
            <div class="tag-input-area">
              <div class="tag-list">
                <span v-for="(p, i) in form.discussion_prompts" :key="i" class="tag-item">
                  {{ p }}
                  <button
                    v-if="template.status === 'draft'"
                    @click="removeItem(form.discussion_prompts, i)"
                    class="tag-remove"
                  >&times;</button>
                </span>
              </div>
              <input
                v-if="template.status === 'draft'"
                v-model="promptInput"
                @keydown.enter.prevent="addItem(form.discussion_prompts, promptInput, () => promptInput = '')"
                type="text"
                class="input"
                :placeholder="$t('bo.sessionTemplates.pressEnterToAdd')"
              />
            </div>
          </div>

          <div class="form-group">
            <label>{{ $t('bo.sessionTemplates.labelCompetencies') }}</label>
            <div class="tag-input-area">
              <div class="tag-list">
                <span v-for="(c, i) in form.competencies" :key="i" class="tag-item">
                  {{ c }}
                  <button
                    v-if="template.status === 'draft'"
                    @click="removeItem(form.competencies, i)"
                    class="tag-remove"
                  >&times;</button>
                </span>
              </div>
              <input
                v-if="template.status === 'draft'"
                v-model="competencyInput"
                @keydown.enter.prevent="addItem(form.competencies, competencyInput, () => competencyInput = '')"
                type="text"
                class="input"
                :placeholder="$t('bo.sessionTemplates.pressEnterToAdd')"
              />
            </div>
          </div>

          <div class="form-group">
            <label>{{ $t('bo.sessionTemplates.labelTags') }}</label>
            <div class="tag-input-area">
              <div class="tag-list">
                <span v-for="(t, i) in form.tags" :key="i" class="tag-item">
                  {{ t }}
                  <button
                    v-if="template.status === 'draft'"
                    @click="removeItem(form.tags, i)"
                    class="tag-remove"
                  >&times;</button>
                </span>
              </div>
              <input
                v-if="template.status === 'draft'"
                v-model="tagInput"
                @keydown.enter.prevent="addItem(form.tags, tagInput, () => tagInput = '')"
                type="text"
                class="input"
                :placeholder="$t('bo.sessionTemplates.pressEnterToAdd')"
              />
            </div>
          </div>

          <div class="section-divider">{{ $t('bo.sessionTemplates.sectionMilestone') }}</div>

          <div class="form-group">
            <label>{{ $t('bo.sessionTemplates.labelMilestoneTitle') }}</label>
            <input
              v-model="form.meta_data.milestone_title"
              type="text"
              class="input"
              :disabled="template.status !== 'draft'"
              :placeholder="$t('bo.sessionTemplates.milestoneTitlePlaceholder')"
              maxlength="200"
            />
          </div>

          <div class="form-group">
            <label>{{ $t('bo.sessionTemplates.labelMilestoneDueDate') }}</label>
            <input
              v-model="form.meta_data.milestone_due_date"
              type="date"
              class="input input-sm"
              :disabled="template.status !== 'draft'"
            />
          </div>

          <div v-if="otherLocales.length" class="localization">
            <div class="loc-head">
              <h3 class="loc-title">{{ $t('bo.sessionTemplates.localizationTitle') }}</h3>
              <button type="button" class="btn-generate" :disabled="localizing" @click="generate">
                {{ localizing ? $t('bo.sessionTemplates.generating') : $t('bo.sessionTemplates.generateTranslations') }}
              </button>
            </div>
            <p class="loc-hint">{{ $t('bo.sessionTemplates.localizationHint') }}</p>

            <div class="loc-tabs">
              <button
                v-for="l in otherLocales"
                :key="l.code"
                type="button"
                class="loc-tab"
                :class="{ active: activeLocale === l.code }"
                @click="activeLocale = l.code"
              >
                {{ l.label || l.code }}
                <span v-if="isAuto(l.code)" class="auto-badge">{{ $t('bo.sessionTemplates.autoBadge') }}</span>
              </button>
            </div>

            <template v-if="activeLocale && translations[activeLocale]">
              <div class="form-group">
                <label>{{ $t('bo.sessionTemplates.labelTitle') }}</label>
                <input v-model="translations[activeLocale].title" type="text" class="input" maxlength="200" />
              </div>
              <div class="form-group">
                <label>{{ $t('bo.sessionTemplates.labelDescription') }}</label>
                <textarea v-model="translations[activeLocale].description" class="textarea" rows="3" />
              </div>
              <div class="form-group">
                <label>{{ $t('bo.sessionTemplates.labelPrompts') }}</label>
                <div class="tag-input-area">
                  <div class="tag-list">
                    <span
                      v-for="(p, i) in translations[activeLocale].discussion_prompts"
                      :key="i"
                      class="tag-item"
                    >
                      {{ p }}
                      <button
                        type="button"
                        class="tag-remove"
                        @click="removeItem(translations[activeLocale].discussion_prompts, i)"
                      >&times;</button>
                    </span>
                  </div>
                  <input
                    v-model="translationPromptInput"
                    type="text"
                    class="input"
                    :placeholder="$t('bo.sessionTemplates.pressEnterToAdd')"
                    @keydown.enter.prevent="addTranslationPrompt"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.close') }}</button>
          <button
            v-if="canSave"
            @click="save"
            :disabled="loading || (isDraft && !form.title?.trim())"
            class="btn-submit"
          >
            {{ loading ? $t('bo.common.saving') : $t('bo.common.saveChanges') }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import sessionTemplateService from '@/services/sessionTemplateService'
import translationService from '@/services/translationService'

const { t } = useI18n()

const props = defineProps({ templateId: { type: Number, required: true } })
const emit = defineEmits(['close', 'updated'])

const template = ref(null)
const loadingTemplate = ref(true)
const loading = ref(false)
const actionLoading = ref(false)
const error = ref('')
const success = ref('')
const promptInput = ref('')
const competencyInput = ref('')
const tagInput = ref('')
const translationPromptInput = ref('')

const form = ref({
  title: '',
  description: '',
  estimated_duration: null,
  discussion_prompts: [],
  competencies: [],
  tags: [],
  meta_data: {
    milestone_title: '',
    milestone_due_date: '',
  },
})

const locales = ref([])
const defaultLocale = ref('en')
const translations = ref({})
const original = ref({})
const activeLocale = ref('')
const localizing = ref(false)

const otherLocales = computed(() => locales.value.filter((l) => l.code !== defaultLocale.value))
const isDraft = computed(() => template.value?.status === 'draft')
const canSave = computed(() => {
  if (isDraft.value) return true
  return otherLocales.value.length > 0
})

const isAuto = (code) => !!translations.value[code]?.is_auto_generated

const emptyTranslation = () => ({
  title: '',
  description: '',
  discussion_prompts: [],
  is_auto_generated: false,
})

const loadLocales = async () => {
  try {
    const data = await translationService.getLocales()
    locales.value = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
    defaultLocale.value = locales.value.find((l) => l.is_default)?.code || 'en'
  } catch {
    locales.value = []
  }
}

const loadTranslations = async () => {
  try {
    const res = await sessionTemplateService.getTranslations(props.templateId)
    const rows = res.data || []
    const map = {}
    rows.forEach((r) => {
      map[r.locale] = {
        title: r.title || '',
        description: r.description || '',
        discussion_prompts: Array.isArray(r.discussion_prompts) ? [...r.discussion_prompts] : [],
        is_auto_generated: !!r.is_auto_generated,
      }
    })
    otherLocales.value.forEach((l) => {
      if (!map[l.code]) map[l.code] = emptyTranslation()
    })
    translations.value = map
    original.value = JSON.parse(JSON.stringify(map))
  } catch {
    // Section localisation non bloquante.
  }
}

const applySourceFieldsFromTranslations = () => {
  const source = translations.value[defaultLocale.value]
  if (!source) return
  if (source.title) form.value.title = source.title
  if (source.description) form.value.description = source.description
  if (Array.isArray(source.discussion_prompts)) {
    form.value.discussion_prompts = [...source.discussion_prompts]
  }
}

const generate = async () => {
  localizing.value = true
  error.value = ''
  success.value = ''
  try {
    await sessionTemplateService.localize(props.templateId)
    await loadTranslations()
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.sessionTemplates.failedLocalize')
  } finally {
    localizing.value = false
  }
}

const translationChanged = (code) => {
  const cur = translations.value[code]
  const ori = original.value[code]
  if (!cur) return false
  if (!ori) {
    return !!(cur.title?.trim() || cur.description?.trim() || (cur.discussion_prompts || []).length)
  }
  return (
    cur.title !== ori.title ||
    cur.description !== ori.description ||
    JSON.stringify(cur.discussion_prompts || []) !== JSON.stringify(ori.discussion_prompts || [])
  )
}

const persistTranslations = async () => {
  for (const l of otherLocales.value) {
    const cur = translations.value[l.code]
    if (!cur || !translationChanged(l.code)) continue
    const prompts = (cur.discussion_prompts || []).map((p) => String(p).trim()).filter(Boolean)
    if (!cur.title?.trim() && !cur.description?.trim() && !prompts.length) continue
    await sessionTemplateService.saveTranslation(props.templateId, l.code, {
      title: cur.title?.trim() || null,
      description: cur.description?.trim() || null,
      discussion_prompts: prompts,
    })
  }
}

const loadTemplate = async () => {
  loadingTemplate.value = true
  try {
    const res = await sessionTemplateService.getTemplate(props.templateId)
    template.value = res.data
    form.value = {
      title: res.data.title || '',
      description: res.data.description || '',
      estimated_duration: res.data.estimated_duration || null,
      discussion_prompts: [...(res.data.discussion_prompts || [])],
      competencies: [...(res.data.competencies || [])],
      tags: [...(res.data.tags || [])],
      meta_data: {
        milestone_title: res.data.meta_data?.milestone_title || '',
        milestone_due_date: res.data.meta_data?.milestone_due_date || '',
      },
    }
  } catch {
    error.value = t('bo.sessionTemplates.failedLoad')
  } finally {
    loadingTemplate.value = false
  }
}

const addItem = (arr, inputVal, clear) => {
  const val = inputVal.trim()
  if (val && !arr.includes(val)) {
    arr.push(val)
    clear()
  }
}

const removeItem = (arr, i) => arr.splice(i, 1)

const addTranslationPrompt = () => {
  const cur = translations.value[activeLocale.value]
  if (!cur) return
  if (!Array.isArray(cur.discussion_prompts)) cur.discussion_prompts = []
  addItem(cur.discussion_prompts, translationPromptInput.value, () => {
    translationPromptInput.value = ''
  })
}

const save = async () => {
  loading.value = true
  error.value = ''
  success.value = ''
  try {
    if (isDraft.value) {
      const payload = { ...form.value }
      if (!payload.estimated_duration) delete payload.estimated_duration
      if (!payload.meta_data.milestone_title && !payload.meta_data.milestone_due_date) {
        delete payload.meta_data
      }
      await sessionTemplateService.updateTemplate(props.templateId, payload)
    }
    await persistTranslations()
    success.value = t('bo.sessionTemplates.saveSuccess')
    await loadTemplate()
    await loadTranslations()
    applySourceFieldsFromTranslations()
    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.sessionTemplates.failedSave')
  } finally {
    loading.value = false
  }
}

const handlePublish = async () => {
  if (!confirm(t('bo.sessionTemplates.confirmPublishEdit'))) return
  actionLoading.value = true
  error.value = ''
  try {
    await sessionTemplateService.publishTemplate(props.templateId)
    await loadTemplate()
    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.sessionTemplates.failedPublish')
  } finally {
    actionLoading.value = false
  }
}

const handleArchive = async () => {
  if (!confirm(t('bo.sessionTemplates.confirmArchiveEdit'))) return
  actionLoading.value = true
  error.value = ''
  try {
    await sessionTemplateService.archiveTemplate(props.templateId)
    await loadTemplate()
    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.sessionTemplates.failedArchive')
  } finally {
    actionLoading.value = false
  }
}

onMounted(async () => {
  await loadTemplate()
  await loadLocales()
  await loadTranslations()
  applySourceFieldsFromTranslations()
  if (!activeLocale.value) activeLocale.value = otherLocales.value[0]?.code || ''
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 0 0.25rem;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.loading-state {
  color: #6b7280;
  text-align: center;
  padding: 3rem;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.success-banner {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.status-actions {
  display: flex;
  gap: 0.5rem;
}

.badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.badge-draft { background: #fef3c7; color: #92400e; }
.badge-published { background: #d1fae5; color: #065f46; }
.badge-archived { background: #f3f4f6; color: #6b7280; }

.btn-action {
  padding: 0.3rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  cursor: pointer;
  border: 1px solid;
  font-weight: 500;
  transition: all 0.15s;
}

.btn-action:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-publish { border-color: #10b981; color: #10b981; background: white; }
.btn-publish:hover:not(:disabled) { background: #ecfdf5; }

.btn-archive { border-color: #f59e0b; color: #f59e0b; background: white; }
.btn-archive:hover:not(:disabled) { background: #fffbeb; }

.info-notice {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.required { color: #dc2626; }

.input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  width: 100%;
  box-sizing: border-box;
}

.input:focus {
  outline: none;
  border-color: #3b82f6;
}

.input:disabled { background: #f9fafb; color: #6b7280; }
.input-sm { max-width: 160px; }

.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  resize: vertical;
  font-family: inherit;
}

.textarea:focus { outline: none; border-color: #3b82f6; }
.textarea:disabled { background: #f9fafb; color: #6b7280; }

.tag-input-area {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  min-height: 1rem;
}

.tag-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.6rem;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 9999px;
  font-size: 0.8rem;
}

.tag-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: #1d4ed8;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}

.btn-cancel:hover { background: #f9fafb; }

.btn-submit {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}

.btn-submit:hover:not(:disabled) { background: #2563eb; }
.btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }

.section-divider {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  margin-top: 0.25rem;
}

.localization {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: #f9fafb;
}

.loc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.loc-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.loc-hint {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0;
}

.btn-generate {
  padding: 0.4rem 0.9rem;
  border: 1px solid #3b82f6;
  background: white;
  color: #2563eb;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.btn-generate:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loc-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.loc-tab {
  padding: 0.3rem 0.7rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.loc-tab.active {
  border-color: #3b82f6;
  color: #2563eb;
  background: #eff6ff;
}

.auto-badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #92400e;
  background: #fef3c7;
  padding: 0.05rem 0.35rem;
  border-radius: 9999px;
}
</style>
